/**
 * 検査設定ページ
 *
 * - 患者選択（URLパラメータまたは選択UI）
 * - プリセット選択
 * - カスタム設定入力
 * - 検査開始
 */

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useApi } from "../hooks/useApi";
import { api, ApiError } from "../services/api";
import { offlineStorage } from "../services/offlineStorage";
import { syncPendingPresetSyncs } from "../services/syncService";
import { getCalibration, mmToPx } from "../utils/calibration";
import type { CreatePatientRequest, ExamPreset, ExamSettings, Patient } from "../types";

// デフォルトの検査設定
const DEFAULT_SETTINGS: ExamSettings = {
  trialCount: 1,
  lineLengths: [80],
  lineThickness: 0.5,
  abnormalThreshold: 5,
  inputMode: "touch",
  showProgressBar: true,
};

function normalizeSettingsForExam(settings: ExamSettings): ExamSettings {
  return {
    ...settings,
    disableAbnormalJudgment: settings.disableAbnormalJudgment ?? false,
  };
}

function ExamSettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const patientIdFromUrl = searchParams.get("patientId");

  // 状態管理
  const [selectedPatientId, setSelectedPatientId] = useState<string>(
    patientIdFromUrl || "",
  );
  const [selectedPresetId, setSelectedPresetId] = useState<string>("");
  const [settings, setSettings] = useState<ExamSettings>(DEFAULT_SETTINGS);
  const [useCustomSettings, setUseCustomSettings] = useState(false);
  const [customLengthInput, setCustomLengthInput] = useState<string>("");
  const [lineThicknessInput, setLineThicknessInput] = useState<string>(
    String(DEFAULT_SETTINGS.lineThickness),
  );

  // プリセット管理用ステート
  const [showSavePresetDialog, setShowSavePresetDialog] = useState(false);
  const [presetNameInput, setPresetNameInput] = useState("");
  const [presetIsDefault, setPresetIsDefault] = useState(false);
  const [editingPresetId, setEditingPresetId] = useState<string | null>(null);
  const [presetSaveNotice, setPresetSaveNotice] = useState<string | null>(null);
  const [presetSaveError, setPresetSaveError] = useState<string | null>(null);
  const [showPresetPickerModal, setShowPresetPickerModal] = useState(false);
  const [isSavingPreset, setIsSavingPreset] = useState(false);
  const [activeQuickModal, setActiveQuickModal] = useState<
    | null
    | "trialCount"
    | "threshold"
    | "inputMode"
    | "autoAdvance"
    | "lineThickness"
  >(null);
  const [showLineLengthModal, setShowLineLengthModal] = useState(false);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [pendingLocalPatients, setPendingLocalPatients] = useState<Patient[]>([]);

  // API呼び出し
  const { data: patients, loading: patientsLoading } =
    useApi<Patient[]>("/api/patients");
  const {
    data: presets,
    loading: presetsLoading,
    refetch: refetchPresets,
  } = useApi<ExamPreset[]>("/api/presets");
  const availablePatients = [
    ...(patients ?? []).filter((patient) => !patient.isDeleted),
    ...pendingLocalPatients,
  ];

  // URLパラメータから患者IDが指定された場合の処理
  useEffect(() => {
    if (patientIdFromUrl) {
      setSelectedPatientId(patientIdFromUrl);
    }
  }, [patientIdFromUrl]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!isOnline) return;

    syncPendingPresetSyncs()
      .then((result) => {
        if (result.synced > 0) {
          refetchPresets();
        }
      })
      .catch((error) => {
        console.error("Preset sync on reconnect failed:", error);
      });
  }, [isOnline, refetchPresets]);

  useEffect(() => {
    const mapPendingToPatient = (
      tempId: string,
      payload: CreatePatientRequest,
      savedAt: string,
    ): Patient => ({
      id: tempId,
      facilityId: "offline",
      patientCode: payload.patientCode,
      name: payload.name,
      nameKana: payload.nameKana || null,
      memo: payload.memo || null,
      isDeleted: false,
      createdAt: savedAt,
      updatedAt: savedAt,
    });

    offlineStorage
      .getAllPendingPatientCreates()
      .then((items) => {
        setPendingLocalPatients(
          items
            .map((item) => mapPendingToPatient(item.tempId, item.payload, item.savedAt))
            .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)),
        );
      })
      .catch((error) => {
        console.error("Failed to load offline patients for exam settings:", error);
      });
  }, []);

  // プリセット選択時の処理
  useEffect(() => {
    if (selectedPresetId && presets) {
      const preset = presets.find((p) => p.id === selectedPresetId);
      if (preset) {
        setSettings(normalizeSettingsForExam(preset.settings));
        setUseCustomSettings(false);
      }
    }
  }, [selectedPresetId, presets]);

  // 初期表示時にデフォルトプリセットを選択（presetsロード完了時の1回のみ）
  useEffect(() => {
    if (
      presets &&
      presets.length > 0 &&
      !selectedPresetId &&
      !useCustomSettings
    ) {
      const defaultPreset = presets.find((p) => p.isDefault);
      if (defaultPreset) {
        setSelectedPresetId(defaultPreset.id);
        setSettings(normalizeSettingsForExam(defaultPreset.settings));
      }
    }
  }, [presets]); // eslint-disable-line react-hooks/exhaustive-deps

  // 設定変更ハンドラ
  const handleSettingChange = <K extends keyof ExamSettings>(
    key: K,
    value: ExamSettings[K],
  ) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setUseCustomSettings(true);
    setSelectedPresetId("");
  };

  // 任意の長さを追加（追加順を維持）
  const handleAddCustomLength = () => {
    const v = parseInt(customLengthInput);
    if (isNaN(v) || v < 1 || v > 300) return;
    if (settings.lineLengths.includes(v)) {
      setCustomLengthInput("");
      return;
    }
    setSettings((prev) => ({
      ...prev,
      lineLengths: [...prev.lineLengths, v],
    }));
    setCustomLengthInput("");
    setUseCustomSettings(true);
    setSelectedPresetId("");
  };

  // 線の長さを個別削除
  const handleRemoveLength = (length: number) => {
    if (settings.lineLengths.length <= 1) return;
    setSettings((prev) => ({
      ...prev,
      lineLengths: prev.lineLengths.filter((l) => l !== length),
    }));
    setUseCustomSettings(true);
    setSelectedPresetId("");
  };

  // 線の長さの並び替え
  const handleMoveLength = (index: number, direction: "up" | "down") => {
    setSettings((prev) => {
      const arr = [...prev.lineLengths];
      const target = direction === "up" ? index - 1 : index + 1;
      if (target < 0 || target >= arr.length) return prev;
      [arr[index], arr[target]] = [arr[target], arr[index]];
      return { ...prev, lineLengths: arr };
    });
    setUseCustomSettings(true);
    setSelectedPresetId("");
  };

  const ensureCustomLineLengthSettings = () => {
    setSettings((prev) => {
      const defaultLen = prev.lineLengths[0] || 80;
      const fallbackPlan = [[defaultLen]];
      return {
        ...prev,
        lineLengthMode: "custom",
        multiLineMode: false,
        multiLineCount: 1,
        trialCount:
          prev.trialPlan && prev.trialPlan.length > 0
            ? prev.trialPlan.length
            : 1,
        trialPlan:
          prev.trialPlan && prev.trialPlan.length > 0
            ? prev.trialPlan
            : fallbackPlan,
      };
    });
    setUseCustomSettings(true);
    setSelectedPresetId("");
  };

  // プリセット保存
  const handleSavePreset = async () => {
    if (!presetNameInput.trim() || isSavingPreset) return;
    setPresetSaveNotice(null);
    setPresetSaveError(null);
    setIsSavingPreset(true);

    const payload = {
      name: presetNameInput.trim(),
      settings: normalizeSettingsForExam(settings),
      isDefault: presetIsDefault,
    };

    try {
      let savedPreset: ExamPreset;
      if (editingPresetId) {
        savedPreset = await api.put<ExamPreset>(`/api/presets/${editingPresetId}`, payload);
      } else {
        savedPreset = await api.post<ExamPreset>("/api/presets", payload);
      }

      setShowSavePresetDialog(false);
      setPresetNameInput("");
      setPresetIsDefault(false);
      setEditingPresetId(null);
      setSelectedPresetId(savedPreset.id);
      setUseCustomSettings(false);
      setSettings(normalizeSettingsForExam(savedPreset.settings));
      setPresetSaveNotice(null);
      refetchPresets();
      return;
    } catch (error: unknown) {
      const canQueueOffline = !isOnline || !navigator.onLine;

      if (error instanceof ApiError) {
        if (error.status < 500 || !canQueueOffline) {
          setPresetSaveError(error.message || "設定の保存に失敗しました");
          return;
        }
      } else if (!canQueueOffline) {
        setPresetSaveError("設定の保存に失敗しました。接続状態を確認してください。");
        return;
      }

      try {
        await offlineStorage.saveLatestExamSettings(payload.settings);
        await offlineStorage.savePendingPresetSync({
          action: editingPresetId ? "update" : "create",
          presetId: editingPresetId ?? undefined,
          payload,
        });
        setUseCustomSettings(true);
        setSelectedPresetId("");
        setPresetSaveNotice(
          "[オフライン保存しました。 オンライン復帰時に自動同期されます]",
        );
        return;
      } catch (offlineError) {
        console.error("Offline preset save failed:", offlineError);
      }

      if (error instanceof ApiError) {
        setPresetSaveError(error.message || "設定の保存に失敗しました");
      } else {
        setPresetSaveError("設定の保存に失敗しました");
      }
    } finally {
      setIsSavingPreset(false);
    }
  };

  // プリセット編集開始
  const handleEditPreset = (preset: ExamPreset) => {
    setEditingPresetId(preset.id);
    setPresetNameInput(preset.name);
    setPresetIsDefault(preset.isDefault);
    setPresetSaveNotice(null);
    setPresetSaveError(null);
    setSettings(normalizeSettingsForExam(preset.settings));
    setShowSavePresetDialog(true);
  };

  // プリセット削除
  const handleDeletePreset = async (presetId: string) => {
    if (!confirm("このプリセットを削除しますか？")) return;
    await api.delete(`/api/presets/${presetId}`);
    if (selectedPresetId === presetId) {
      setSelectedPresetId("");
      setSettings(DEFAULT_SETTINGS);
    }
    refetchPresets();
  };

  // 検査開始
  const handleStartExam = () => {
    if (!selectedPatientId || !selectedPatient) {
      alert("患者を選択してください");
      return;
    }

    // ExamPrepareState形式で location.state として渡す
    const defaultLen = settings.lineLengths[0] || 80;
    const normalizedSettings = normalizeSettingsForExam({
      ...settings,
      lineLengthMode: "custom",
      multiLineMode: false,
      multiLineCount: 1,
      trialPlan:
        settings.trialPlan && settings.trialPlan.length > 0
          ? settings.trialPlan
          : [[defaultLen]],
      trialCount:
        settings.trialPlan && settings.trialPlan.length > 0
          ? settings.trialPlan.length
          : 1,
      lineLengths:
        settings.lineLengths && settings.lineLengths.length > 0
          ? settings.lineLengths
          : [80],
    });

    navigate("/exam/prepare", {
      state: {
        patient: selectedPatient,
        settings: normalizedSettings,
      },
    });
  };

  const selectedPatient = availablePatients.find((p) => p.id === selectedPatientId);
  const selectedPreset = presets?.find((p) => p.id === selectedPresetId);
  const defaultPreset = presets?.find((p) => p.isDefault);
  const displayPresetName =
    selectedPreset?.name || defaultPreset?.name || "デフォルト";
  const calibration = getCalibration();
  const lineThicknessPreviewPx = Math.max(
    1,
    mmToPx(settings.lineThickness, calibration.ppmm),
  );

  const openQuickModal = (
    modal:
      | "trialCount"
      | "threshold"
      | "inputMode"
      | "autoAdvance"
      | "lineThickness",
  ) => {
    if (modal === "lineThickness") {
      setLineThicknessInput(String(settings.lineThickness));
    }
    setActiveQuickModal(modal);
  };

  const commitLineThicknessInput = () => {
    const v = parseFloat(lineThicknessInput);
    if (isNaN(v)) {
      setLineThicknessInput(String(settings.lineThickness));
      return;
    }
    const next = Math.max(0.1, Number(v.toFixed(2)));
    handleSettingChange("lineThickness", next);
    setLineThicknessInput(String(next));
  };

  useEffect(() => {
    const modalOpen = Boolean(
      showSavePresetDialog ||
      showPresetPickerModal ||
      showLineLengthModal ||
      activeQuickModal,
    );
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevOverscroll = document.body.style.overscrollBehavior;
    const prevTouchAction = document.body.style.touchAction;
    const prevPosition = document.body.style.position;
    const prevTop = document.body.style.top;
    const prevWidth = document.body.style.width;
    const prevLeft = document.body.style.left;
    const prevRight = document.body.style.right;
    const scrollY = window.scrollY;
    if (modalOpen) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
      document.body.style.overscrollBehavior = "none";
      document.body.style.touchAction = "none";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";
    }
    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overscrollBehavior = prevOverscroll;
      document.body.style.touchAction = prevTouchAction;
      document.body.style.position = prevPosition;
      document.body.style.top = prevTop;
      document.body.style.width = prevWidth;
      document.body.style.left = prevLeft;
      document.body.style.right = prevRight;
      if (modalOpen) {
        window.scrollTo(0, scrollY);
      }
    };
  }, [
    showSavePresetDialog,
    showPresetPickerModal,
    showLineLengthModal,
    activeQuickModal,
  ]);

  // SYSTEM_ADMINは検査実施不可（仕様: 権限マトリクス）
  if (user?.role === "SYSTEM_ADMIN") {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <h2 className="text-yellow-800 font-semibold mb-2">
            検査を実施できません
          </h2>
          <p className="text-yellow-600 mb-4">
            システム管理者は検査を実施する権限がありません。
          </p>
          <button
            onClick={() => navigate("/home")}
            className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
          >
            ホームに戻る
          </button>
        </div>
      </div>
    );
  }

  // ローディング表示
  if (patientsLoading || presetsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-2 md:px-0 py-2 md:py-4">
      <div className="mb-6 md:mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">設定サマリー</h1>
        <p className="text-slate-500">現在の検査設定を確認できます</p>
      </div>

      <div className="space-y-6">
        {/* 患者選択セクション */}
        <section className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">患者選択</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              患者を選択 <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedPatientId}
              onChange={(e) => setSelectedPatientId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">-- 患者を選択してください --</option>
              {availablePatients.map((patient) => (
                <option key={patient.id} value={patient.id}>
                  {patient.name} ({patient.patientCode})
                  {patient.id.startsWith("offline-patient-") ? " [オフライン保存]" : ""}
                </option>
              ))}
            </select>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-6">
          <h2 className="flex items-center gap-3 text-2xl font-bold text-slate-900 mb-5">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-blue-600 text-base">
              ●
            </span>
            プリセット
          </h2>
          <div className="rounded-xl border border-slate-200 p-4 flex items-center justify-between">
            <button
              onClick={() => setShowPresetPickerModal(true)}
              className="flex items-center gap-2 flex-wrap text-left hover:text-blue-700"
            >
              <span className="text-slate-600">使用中のプリセット</span>
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                {displayPresetName}
              </span>
              <span className="text-slate-500 text-sm">
                {useCustomSettings || !selectedPresetId
                  ? "(変更あり)"
                  : "(変更なし)"}
              </span>
              <span className="font-semibold text-slate-700">＞</span>
            </button>
            <button
              onClick={() => {
                setEditingPresetId(null);
                setPresetNameInput("");
                setPresetIsDefault(false);
                setPresetSaveNotice(null);
                setPresetSaveError(null);
                setShowSavePresetDialog(true);
              }}
              className="text-sm px-4 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200"
            >
              保存
            </button>
          </div>
        </section>

        {/* プリセット保存ダイアログ */}
        {showPresetPickerModal && (
          <div className="fixed inset-0 bg-black/35 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-2xl mx-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                プリセットを選択
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
                {presets?.map((preset) => (
                  <button
                    key={preset.id}
                    onClick={() => {
                      setSelectedPresetId(preset.id);
                      setUseCustomSettings(false);
                      setShowPresetPickerModal(false);
                    }}
                    className={`rounded-xl border p-4 text-left ${selectedPresetId === preset.id ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-slate-900">
                        {preset.name}
                      </span>
                      {preset.isDefault && (
                        <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">
                          デフォルト
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">
                      試行回数: {preset.settings.trialCount}回
                    </p>
                    <p className="text-sm text-slate-500">
                      線の長さ: {preset.settings.lineLengths.join(", ")}mm
                    </p>
                    <div className="mt-3 flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditPreset(preset);
                          setShowPresetPickerModal(false);
                        }}
                        className="px-3 py-1 text-xs text-blue-600 border border-blue-300 rounded hover:bg-blue-50"
                      >
                        編集
                      </button>
                      {!preset.isDefault && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePreset(preset.id);
                          }}
                          className="px-3 py-1 text-xs text-red-600 border border-red-300 rounded hover:bg-red-50"
                        >
                          削除
                        </button>
                      )}
                    </div>
                  </button>
                ))}
                <button
                  onClick={() => {
                    setSelectedPresetId("");
                    setUseCustomSettings(true);
                    setShowPresetPickerModal(false);
                  }}
                  className={`rounded-xl border p-4 text-left ${useCustomSettings && !selectedPresetId ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}
                >
                  <div className="font-semibold text-slate-900 mb-1">
                    カスタム設定
                  </div>
                  <p className="text-sm text-slate-500">
                    現在の設定値をそのまま使います
                  </p>
                </button>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowPresetPickerModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  閉じる
                </button>
              </div>
            </div>
          </div>
        )}

        {/* プリセット保存ダイアログ */}
        {showSavePresetDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {editingPresetId ? "プリセットを編集" : "プリセットとして保存"}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    プリセット名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={presetNameInput}
                    onChange={(e) => setPresetNameInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSavePreset();
                    }}
                    placeholder="例: 標準検査（80mm）"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    autoFocus
                  />
                </div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={presetIsDefault}
                    onChange={(e) => setPresetIsDefault(e.target.checked)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">
                    デフォルトに設定
                  </span>
                </label>
                <div className="bg-gray-50 rounded p-3 text-sm text-gray-600">
                  <p>保存する設定:</p>
                  <p>
                    試行回数: {settings.trialCount}回 / 線の長さ:{" "}
                    {settings.lineLengths.join(", ")}mm
                  </p>
                  <p>閾値: {settings.abnormalThreshold}%</p>
                </div>
                {presetSaveError && (
                  <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
                    <p className="text-red-600 text-sm">{presetSaveError}</p>
                  </div>
                )}
                {presetSaveNotice && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    {presetSaveNotice}
                  </div>
                )}
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowSavePresetDialog(false);
                    setEditingPresetId(null);
                    setPresetNameInput("");
                    setPresetSaveNotice(null);
                    setPresetSaveError(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleSavePreset}
                  disabled={!presetNameInput.trim() || isSavingPreset}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    presetNameInput.trim() && !isSavingPreset
                      ? "bg-blue-600 text-white hover:bg-blue-700"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  {isSavingPreset ? "保存中..." : "保存"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 線の長さと試行の仕方 モーダル */}
        {showLineLengthModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] overscroll-none">
            <div
              className="bg-white rounded-lg shadow-xl p-6 w-full max-w-6xl mx-4 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  線の長さと試行の仕方
                </h3>
                <button
                  onClick={() => setShowLineLengthModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  閉じる
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    線の長さ (mm)
                  </label>
                  <div className="mt-3 flex items-center space-x-2">
                    <input
                      type="number"
                      min="1"
                      max="300"
                      placeholder="任意の長さ (1-300)"
                      value={customLengthInput}
                      onChange={(e) => setCustomLengthInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleAddCustomLength();
                      }}
                      className="w-44 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                    <button
                      onClick={handleAddCustomLength}
                      className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                    >
                      追加
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <label className="block text-xl font-semibold text-slate-900">
                        試行ごとの線の長さ
                      </label>
                      <p className="mt-1 text-sm text-slate-500">
                        各試行で表示する線の長さと本数を設定します。
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => {
                          const lineCount = settings.multiLineMode
                            ? (settings.multiLineCount ?? 2)
                            : 1;
                          const defaultLen = settings.lineLengths[0] || 80;
                          const newPlan = [
                            ...(settings.trialPlan || []),
                            Array.from({ length: lineCount }, () => defaultLen),
                          ];
                          handleSettingChange("trialPlan", newPlan);
                          handleSettingChange("trialCount", newPlan.length);
                        }}
                        className="h-11 px-5 text-base text-blue-600 border border-blue-300 rounded-2xl hover:bg-blue-50"
                      >
                        <span className="inline-flex items-center gap-2">
                          <span className="text-2xl leading-none">＋</span>
                          <span>試行を追加</span>
                        </span>
                      </button>
                      {(settings.trialPlan?.length ?? 0) > 1 && (
                        <button
                          onClick={() => {
                            const newPlan = (settings.trialPlan || []).slice(
                              0,
                              -1,
                            );
                            handleSettingChange("trialPlan", newPlan);
                            handleSettingChange("trialCount", newPlan.length);
                          }}
                          className="h-11 px-5 text-base text-red-600 border border-red-300 rounded-2xl hover:bg-red-50"
                        >
                          末尾を削除
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="border border-slate-200 rounded-2xl overflow-hidden">
                    <div className="bg-slate-50 px-6 py-3 flex">
                      <span className="w-20 text-base font-medium text-slate-800">
                        試行
                      </span>
                      <span className="flex-1 text-base font-medium text-slate-800">
                        線の長さ（mm）
                      </span>
                    </div>
                    <div className="divide-y divide-gray-100 max-h-[420px] overflow-y-auto">
                      {(settings.trialPlan || []).map((lengths, trialIdx) => (
                        <div
                          key={trialIdx}
                          className="px-6 py-4 flex items-center hover:bg-gray-50"
                        >
                          <span className="w-20">
                            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-xl font-semibold text-slate-700">
                              {trialIdx + 1}
                            </span>
                          </span>
                          <div className="flex-1 flex flex-wrap gap-3 items-center">
                            {lengths.map((len, lineIdx) => (
                              <div
                                key={lineIdx}
                                className="inline-flex items-center gap-2"
                              >
                                <select
                                  value={len}
                                  onChange={(e) => {
                                    const newPlan = [
                                      ...(settings.trialPlan || []),
                                    ];
                                    newPlan[trialIdx] = [...newPlan[trialIdx]];
                                    newPlan[trialIdx][lineIdx] = parseInt(
                                      e.target.value,
                                    );
                                    handleSettingChange("trialPlan", newPlan);
                                  }}
                                  className="h-11 min-w-[140px] px-4 rounded-2xl bg-slate-100 border border-slate-200 text-lg text-slate-800"
                                >
                                  {[...new Set([...settings.lineLengths, len])]
                                    .sort((a, b) => a - b)
                                    .map((l) => (
                                      <option key={l} value={l}>
                                        {l}mm
                                      </option>
                                    ))}
                                </select>
                                {lengths.length > 1 && (
                                  <button
                                    onClick={() => {
                                      const newPlan = [
                                        ...(settings.trialPlan || []),
                                      ];
                                      newPlan[trialIdx] = newPlan[
                                        trialIdx
                                      ].filter((_, i) => i !== lineIdx);
                                      handleSettingChange("trialPlan", newPlan);
                                    }}
                                    className="text-red-400 hover:text-red-600 text-2xl leading-none"
                                  >
                                    &times;
                                  </button>
                                )}
                              </div>
                            ))}
                            <button
                              onClick={() => {
                                const newPlan = [...(settings.trialPlan || [])];
                                newPlan[trialIdx] = [
                                  ...newPlan[trialIdx],
                                  settings.lineLengths[0] || 80,
                                ];
                                handleSettingChange("trialPlan", newPlan);
                              }}
                              className="ml-2 text-blue-600 hover:text-blue-700 text-base font-semibold"
                            >
                              ＋ 線を追加
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeQuickModal && (
          <div className="fixed inset-0 bg-black/35 flex items-center justify-center z-[100]">
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {activeQuickModal === "trialCount" && "試行回数を編集"}
                {activeQuickModal === "threshold" && "異常判定閾値を編集"}
                {activeQuickModal === "inputMode" && "入力モードを編集"}
                {activeQuickModal === "autoAdvance" && "自動遷移を編集"}
                {activeQuickModal === "lineThickness" && "線の太さを編集"}
              </h3>

              {activeQuickModal === "trialCount" && (
                <div className="space-y-3">
                  <input
                    type="range"
                    min="1"
                    max="20"
                    value={settings.trialCount}
                    onChange={(e) =>
                      handleSettingChange(
                        "trialCount",
                        parseInt(e.target.value),
                      )
                    }
                    className="w-full"
                  />
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={settings.trialCount}
                    onChange={(e) => {
                      const v = parseInt(e.target.value);
                      if (!isNaN(v))
                        handleSettingChange(
                          "trialCount",
                          Math.max(1, Math.min(20, v)),
                        );
                    }}
                    className="w-24 border border-slate-300 rounded px-2 py-1"
                  />
                </div>
              )}

              {activeQuickModal === "threshold" && (
                <div className="space-y-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={settings.disableAbnormalJudgment || false}
                      onChange={(e) =>
                        handleSettingChange(
                          "disableAbnormalJudgment",
                          e.target.checked,
                        )
                      }
                    />
                    <span className="text-sm text-slate-700">判定しない</span>
                  </label>
                  {!settings.disableAbnormalJudgment && (
                    <>
                      <input
                        type="range"
                        min="1"
                        max="20"
                        value={settings.abnormalThreshold}
                        onChange={(e) =>
                          handleSettingChange(
                            "abnormalThreshold",
                            parseInt(e.target.value),
                          )
                        }
                        className="w-full"
                      />
                      <input
                        type="number"
                        min="1"
                        max="20"
                        value={settings.abnormalThreshold}
                        onChange={(e) => {
                          const v = parseInt(e.target.value);
                          if (!isNaN(v))
                            handleSettingChange(
                              "abnormalThreshold",
                              Math.max(1, Math.min(20, v)),
                            );
                        }}
                        className="w-24 border border-slate-300 rounded px-2 py-1"
                      />
                    </>
                  )}
                </div>
              )}

              {activeQuickModal === "inputMode" && (
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: "touch", label: "タッチ", icon: "👆" },
                    { value: "pen", label: "ペン", icon: "✏️" },
                    { value: "mouse", label: "マウス", icon: "🖱️" },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() =>
                        handleSettingChange(
                          "inputMode",
                          option.value as ExamSettings["inputMode"],
                        )
                      }
                      className={`px-3 py-2 rounded border ${settings.inputMode === option.value ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700 border-slate-300"}`}
                    >
                      <span className="mr-1">{option.icon}</span>
                      {option.label}
                    </button>
                  ))}
                </div>
              )}

              {activeQuickModal === "autoAdvance" && (
                <div className="space-y-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={settings.autoAdvance || false}
                      onChange={(e) =>
                        handleSettingChange("autoAdvance", e.target.checked)
                      }
                    />
                    <span className="text-sm text-slate-700">
                      自動遷移を有効にする
                    </span>
                  </label>
                  {(settings.autoAdvance || false) && (
                    <>
                      <input
                        type="range"
                        min="0"
                        max="10"
                        step="0.5"
                        value={settings.autoAdvanceSeconds ?? 1}
                        onChange={(e) =>
                          handleSettingChange(
                            "autoAdvanceSeconds",
                            parseFloat(e.target.value),
                          )
                        }
                        className="w-full"
                      />
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          max="10"
                          step="0.5"
                          value={settings.autoAdvanceSeconds ?? 1}
                          onChange={(e) => {
                            const v = parseFloat(e.target.value);
                            if (!isNaN(v))
                              handleSettingChange(
                                "autoAdvanceSeconds",
                                Math.max(0, Math.min(10, v)),
                              );
                          }}
                          className="w-24 border border-slate-300 rounded px-2 py-1 text-center"
                        />
                        <span className="text-sm text-slate-600">秒</span>
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeQuickModal === "lineThickness" && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0.1"
                      step="0.1"
                      value={lineThicknessInput}
                      onChange={(e) => setLineThicknessInput(e.target.value)}
                      onBlur={commitLineThicknessInput}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") commitLineThicknessInput();
                      }}
                      className="w-24 border border-slate-300 rounded px-2 py-1 text-center"
                    />
                    <span className="text-sm text-slate-600">mm</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">プレビュー:</span>
                    <div
                      className="w-28 bg-black"
                      style={{ height: `${lineThicknessPreviewPx}px` }}
                    />
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setActiveQuickModal(null)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  閉じる
                </button>
              </div>
            </div>
          </div>
        )}

        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-6">
          <h2 className="flex items-center gap-3 text-2xl font-bold text-slate-900 mb-5">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-blue-600 text-base">
              ●
            </span>
            基本設定
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-200">
              <button
                type="button"
                onClick={() => {
                  ensureCustomLineLengthSettings();
                  setShowLineLengthModal(true);
                }}
                className="w-full px-4 py-4 flex items-center justify-between text-left hover:bg-slate-50 cursor-pointer"
              >
                <span className="text-slate-600">線の長さ / 試行の仕方</span>
                <span className="font-semibold text-slate-900">
                  {settings.lineLengths.join(", ")}mm (指定) ＞
                </span>
              </button>
            </div>
            <div className="rounded-xl border border-slate-200 divide-y divide-slate-200">
              <button
                type="button"
                onClick={() => openQuickModal("threshold")}
                className="w-full px-4 py-4 flex items-center justify-between text-left hover:bg-slate-50 cursor-pointer"
              >
                <span className="text-slate-600">異常閾値</span>
                <span className="font-semibold text-slate-900">
                  {settings.disableAbnormalJudgment
                    ? "判定なし"
                    : `${settings.abnormalThreshold}%`}{" "}
                  ＞
                </span>
              </button>
              <button
                type="button"
                onClick={() => openQuickModal("inputMode")}
                className="w-full px-4 py-4 flex items-center justify-between text-left hover:bg-slate-50 cursor-pointer"
              >
                <span className="text-slate-600">入力モード</span>
                <span className="font-semibold text-slate-900">
                  {settings.inputMode === "touch"
                    ? "タッチ"
                    : settings.inputMode === "pen"
                      ? "ペン"
                      : "マウス"}{" "}
                  ＞
                </span>
              </button>
              <button
                type="button"
                onClick={() => openQuickModal("lineThickness")}
                className="w-full px-4 py-4 flex items-center justify-between text-left hover:bg-slate-50 cursor-pointer"
              >
                <span className="text-slate-600">線の太さ</span>
                <span className="font-semibold text-slate-900">
                  {settings.lineThickness}mm ＞
                </span>
              </button>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-6">
          <h2 className="flex items-center gap-3 text-2xl font-bold text-slate-900 mb-5">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 text-base">
              ●
            </span>
            線の表示位置
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              {
                value: "random",
                label: "ランダム",
                desc: "毎回異なる位置に表示",
              },
              { value: "center", label: "中央", desc: "画面中央に表示" },
              { value: "left", label: "左寄せ", desc: "画面左側に表示" },
              { value: "right", label: "右寄せ", desc: "画面右側に表示" },
            ].map((option) => {
              const enabled =
                (settings.linePositionMode || "random") === option.value;
              return (
                <button
                  key={option.value}
                  onClick={() =>
                    handleSettingChange(
                      "linePositionMode",
                      option.value as "random" | "center" | "left" | "right",
                    )
                  }
                  className="rounded-xl border border-slate-200 p-4 text-left hover:bg-slate-50"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-slate-900">
                      {option.label}
                    </span>
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold ${enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}
                    >
                      {enabled ? "有効" : "無効"}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500">{option.desc}</p>
                </button>
              );
            })}
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-6">
          <h2 className="flex items-center gap-3 text-2xl font-bold text-slate-900 mb-5">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-violet-100 text-violet-600 text-base">
              ●
            </span>
            操作設定
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
              onClick={() =>
                handleSettingChange(
                  "showProgressBar",
                  !settings.showProgressBar,
                )
              }
              className="rounded-xl border border-slate-200 p-4 text-left hover:bg-slate-50"
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-900">
                  進捗バーを表示する
                </span>
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold ${settings.showProgressBar ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}
                >
                  {settings.showProgressBar ? "有効" : "無効"}
                </span>
              </div>
            </button>
            <button
              type="button"
              onClick={() => openQuickModal("autoAdvance")}
              className="rounded-xl border border-slate-200 p-4 text-left hover:bg-slate-50"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-slate-900">
                  マーク後に自動で次の試行へ進む
                </span>
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold ${settings.autoAdvance ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}
                >
                  {settings.autoAdvance ? "有効" : "無効"}
                </span>
              </div>
              <p className="text-sm text-slate-500">
                {settings.autoAdvance
                  ? `${settings.autoAdvanceSeconds ?? 1}秒後に自動遷移`
                  : "タップで設定"}
              </p>
            </button>
          </div>
        </section>

        {/* アクションボタン */}
        <div className="flex justify-between">
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 rounded-lg border border-slate-300 bg-white text-slate-700 font-medium hover:bg-slate-50 transition-colors"
          >
            戻る
          </button>
          <button
            onClick={handleStartExam}
            disabled={!selectedPatientId}
            className={`px-8 py-3 rounded-lg font-medium transition-colors ${
              selectedPatientId
                ? "bg-blue-600 text-white hover:bg-blue-700"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            検査を開始する
          </button>
        </div>
      </div>
    </div>
  );
}

export default ExamSettingsPage;
